<script setup lang="ts">
import { useCurrentUser, useFirebaseAuth } from 'vuefire';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from './core/hooks/useStore';
// import { userMapper } from './core/helpers/mapper.helper';
import Notifications from './components/status/Notifications.vue';
import { User as FUser } from 'firebase/auth';
import { useDarkMode } from './core/composables/useDarkMode';
import OverviewLoader from './components/layout/loaders/OverviewLoader.vue';

import * as Sentry from '@sentry/vue';

const currentFirebaseUser = useCurrentUser();
const route = useRoute();
const router = useRouter();
const { state, dispatch } = useStore();

const loading = ref(true);
const auth = useFirebaseAuth()!; // only exists on client side

watch(currentFirebaseUser, handleCurrentUser);
async function handleCurrentUser(currentUser?: FUser | null, oldCurrentUser?: FUser | null) {
   loading.value = true;
   if (currentUser?.uid) {
      const user = await dispatch('user/getUser', currentUser.uid);
      if (!user) {
         auth.signOut();
         await router.push('/login');
         loading.value = false;
         return;
      }

      Sentry.setUser({
         id: currentUser.uid,
         name: currentUser.displayName ?? currentUser.email,
         email: currentUser.email!,
      });

      await Promise.allSettled([
         dispatch('genre/getMovieGenres'),
         dispatch('genre/getSerieGenres'),
         dispatch('watchStatus/getWatchStatuses'),
      ]);

      await dispatch('user/getUser', currentUser.uid);

      loading.value = false;
   }
   if (!currentUser && oldCurrentUser && route.meta.requiresAuth) {
      Sentry.setUser(null);
      router.push('/login');
      loading.value = false;
   } else {
      loading.value = false;
      if (!currentUser) return router.push('/');
   }
}

// #region NOTIFICATIONS
const notifications = computed(() => state.notification?.notifications);
// #endregion

// #region DARKMODE
onBeforeMount(async () => {
   useDarkMode();
});
// #endregion
</script>
<template>
   <Notifications class="notifications" :notifications="notifications" />

   <OverviewLoader v-if="loading" />
   <RouterView v-else v-slot="{ Component }">
      <transition name="fade">
         <Component :is="Component" />
      </transition>
   </RouterView>
</template>
<style lang="scss" scoped>
.page-loader {
   width: 100dvw;
   height: 100dvh;

   display: flex;
   align-items: center;
   justify-content: center;
}

.notifications {
   transform-origin: right top;

   position: absolute;
   top: 3rem;
   right: 1rem;
   z-index: 100;
}

@media screen and (min-width: 768px) {
   .notifications {
      top: 4rem;
   }
}
</style>
